import { saasApiAxios } from '../lib/axios-interceptor';

export interface Permission {
  name: string;
  granted: boolean;
}

export interface Role {
  id: string;
  customer_id: string;
  name: string;
  description?: string;
  permissions: Record<string, boolean>;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
  permissions: Record<string, boolean>;
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  permissions?: Record<string, boolean>;
}



const baseURL = '/api/roles';

// CRUD functions
export const getRoles = async (): Promise<Role[]> => {
  try {
    const response = await saasApiAxios.get(baseURL);
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

export const getRole = async (id: string): Promise<Role> => {
  try {
    const response = await saasApiAxios.get(`${baseURL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role:', error);
    throw error;
  }
};

export const createRole = async (roleData: CreateRoleRequest): Promise<Role> => {
  try {
    const response = await saasApiAxios.post(baseURL, roleData);
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
};

export const updateRole = async (id: string, roleData: UpdateRoleRequest): Promise<Role> => {
  try {
    const response = await saasApiAxios.put(`${baseURL}/${id}`, roleData);
    return response.data;
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

export const deleteRole = async (id: string): Promise<void> => {
  try {
    await saasApiAxios.delete(`${baseURL}/${id}`);
  } catch (error) {
    console.error('Error deleting role:', error);
    throw error;
  }
};

// Helper functions
export const permissionsToArray = (permissions: Record<string, boolean>): Permission[] => {
  const permissionLabels: Record<string, string> = {
    manage_users: 'User Management',
    role_management: 'Role Management',
    manage_opportunities: 'Manage Opportunities',
    view_opportunities: 'Read Only'
  };

  return Object.entries(permissions).map(([key, value]) => ({
    name: permissionLabels[key] || key,
    granted: value
  }));
};

export const arrayToPermissions = (permissions: Permission[]): Record<string, boolean> => {
  const permissionKeys: Record<string, string> = {
    'User Management': 'manage_users',
    'Role Management': 'role_management',
    'Manage Opportunities': 'manage_opportunities',
    'Read Only': 'view_opportunities'
  };

  const result: Record<string, boolean> = {};
  permissions.forEach(permission => {
    const key = permissionKeys[permission.name] || permission.name;
    result[key] = permission.granted;
  });
  return result;
};

// Export as default object for backward compatibility
const RoleService = {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  permissionsToArray,
  arrayToPermissions
};

export default RoleService; 